import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";

const useShippedDevices = ({ storeId, page = 1, pageSize = 10 } = {}) => {
  const { request } = useApi();
  const [shippedDevices, setShippedDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchShippedDevices = useCallback(
    async (signal) => {
      if (!storeId) {
        setShippedDevices([]);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await request({
          path: "/shipment/shipped-devices",
          params: { page, pageSize },
          headers: { "X-Store-Id": storeId },
          signal,
        });

        setShippedDevices(
          Array.isArray(data?.shippedDevices) ? data.shippedDevices : []
        );
        setTotal(Number.isFinite(data?.total) ? data.total : 0);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setShippedDevices([]);
        setTotal(0);
        setError(err?.message || "Failed to load shipped devices.");
      } finally {
        setIsLoading(false);
      }
    },
    [page, pageSize, request, storeId]
  );

  useEffect(() => {
    if (!storeId) return;
    const controller = new AbortController();
    fetchShippedDevices(controller.signal);
    return () => controller.abort();
  }, [fetchShippedDevices, storeId]);

  const refetch = useCallback(() => {
    const controller = new AbortController();
    fetchShippedDevices(controller.signal);
    return () => controller.abort();
  }, [fetchShippedDevices]);

  return { shippedDevices, total, isLoading, error, refetch };
};

export default useShippedDevices;
